import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import { settingsKeys } from '../components/settings/shared/settings-queries';
import { API_BASE } from './api-base';
import { useGenericBi } from './use-generic-bi';
import { usePageLoadBi } from './use-page-load-bi';
import { useLoadBi } from './use-load-bi';
import { useBasePath } from './use-base-path';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { waitForRecipientVisible } from './wait-for-recipient';
import { useTranslation } from './i18n';
import { logSignupOnce } from './appsflyer';
import { ensureMe } from './me';
import { claimStoredReferral } from './claim-stored-referral';
import { wasCodeSent } from './phone-verification-result';
import { sendVerificationCode } from './send-verification-code';
import { useSendCodeBi } from './use-send-code-bi';

export const CODE_LENGTH = 6;

/** Exported so the page can fire its page-view with the SAME src:172 identity its clicks report. */
export const PHONE_CODE_PAGE = {
  page_name: 'enter_phone_code_page',
  page_version: 'v3_onboarding_enter_phone_code',
} as const;
const PAGE = PHONE_CODE_PAGE;

type CodeMethod = 'sms' | 'voice' | 'whatsapp';
type ResendStatus = 'idle' | 'sending' | 'sent';

// What the current error is ABOUT. Only `code` is a statement about the digits the
// user typed, so only `code` may tint the field or mark it aria-invalid — a failed
// resend or a lost verification session says nothing about what they entered.
type ErrorKind = 'code' | 'resend' | 'session';

/**
 * What was sent, and where the step goes when it finishes.
 *
 * `sent` is supplied by a caller that already holds it in memory. That caller is the reason this is a
 * parameter at all: the number and the verificationId reach the standalone page THROUGH THE URL, and a
 * URL is broadcast — every analytics tag on the page reports the page location, so an
 * `/onboarding/code?phone=…&verificationId=…` load hands both to Facebook, Google Ads, DoubleClick and
 * the GTM container as page-view data. Passing them in memory keeps them off the URL, out of the
 * beacons, out of the browser history and out of any Referer.
 */
export interface CodeVerificationInput {
  sent?: { phone: string; verificationId: string; method: CodeMethod };
  onVerified?: () => void;
  onUseAnotherNumber?: () => void;
}

// Everything the code step does apart from render it — shared by both brand trees
// (see components/{symphony,orion}/onboarding/CodeScreen.tsx). The SMS/voice and
// WhatsApp paths verify in different places (WhatsApp has no verificationId; the
// recipient service checks the code itself and rejects with HTTP 400), and the
// 409 phone-in-use branch is a distinct outcome rather than an error — so this
// behaviour is kept in one place.
export function useCodeVerification({ sent, onVerified, onUseAnotherNumber }: CodeVerificationInput = {}) {
  const router = useRouter();
  const basePath = useBasePath();
  const queryClient = useQueryClient();
  const { activeConversationId, setActiveConversationId } = useActiveConversation();
  // The thread the verification step was opened over — the URL is authoritative, since that is what the
  // step was navigated to with, and the context is the fallback for a step opened without one.
  const conversationId = typeof router.query.conversation === 'string' ? router.query.conversation : (activeConversationId ?? '');
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('onboarding-code');
  const logSendCode = useSendCodeBi('onboarding-code');

  usePageLoadBi(PAGE);

  const phone = sent ? sent.phone : typeof router.query.phone === 'string' ? router.query.phone : '';
  const initialVerificationId = sent
    ? sent.verificationId
    : typeof router.query.verificationId === 'string'
      ? router.query.verificationId
      : '';
  const initialMethod: CodeMethod = sent
    ? sent.method
    : router.query.method === 'voice'
      ? 'voice'
      : router.query.method === 'whatsapp'
        ? 'whatsapp'
        : 'sms';

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState<{ message: string; kind: ErrorKind } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [method, setMethod] = useState<CodeMethod>(initialMethod);
  const [resend, setResend] = useState<ResendStatus>('idle');
  const [verificationId, setVerificationId] = useState(initialVerificationId);
  const [phoneInUse, setPhoneInUse] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  // Re-entry guard for the auto-submit effect. Using a ref instead of `submitting`
  // in the dep array stops the effect from re-running when we flip submitting=true
  // mid-flow — that re-run was firing the cleanup and short-circuiting the
  // still-in-flight recipient call.
  const inFlightRef = useRef(false);

  // Keep verificationId/method in sync with the query param if the page is arrived at
  // directly (e.g. iteration tab) and the param changes.
  useEffect(() => {
    if (!router.isReady) return;
    if (initialVerificationId && initialVerificationId !== verificationId) setVerificationId(initialVerificationId);
    if (initialMethod !== method) setMethod(initialMethod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, initialVerificationId, initialMethod]);

  const value = digits.join('');
  const complete = value.length === CODE_LENGTH && /^\d{6}$/.test(value);

  // Tracks the /api/verify-code (+ /api/recipient) submit region; on success the page
  // navigates away, so loadFinish fires only on a failure/phone-in-use settle.
  useLoadBi('code-verification', { isLoading: submitting, isError: !!error });

  // Verification runs off the six digits being complete — mirroring mobile, which submits as
  // soon as the code is in rather than waiting for a press. Also exposed so the Verify button
  // and Enter submit the same way; in practice auto-submit gets there first, so the button
  // spends most of its life disabled and is the affordance rather than the trigger.
  function submit() {
    if (!complete || inFlightRef.current) return;
    const isWhatsApp = method === 'whatsapp';
    // WhatsApp has no verify-code step — the recipient service verifies the code
    // itself — so only the SMS/voice path needs a verificationId up front.
    if (!isWhatsApp && !verificationId) {
      setError({ message: t('onboarding.code.error.missingVerificationId'), kind: 'session' });
      // Clear, like every other rejection: leaving them on screen meant the next successful
      // resend re-armed this effect (it is keyed on verificationId) and spent the freshly sent
      // code on the stale digits, reporting "wrong code" before the SMS had even arrived.
      setDigits(Array(CODE_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
      return;
    }
    inFlightRef.current = true;
    setSubmitting(true);
    setError(null);
    logPageClickBi({ ...PAGE, button_name: 'verify' });
    (async () => {
      try {
        if (!isWhatsApp) {
          const verifyRes = await fetch(`${API_BASE}/api/verify-code`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: value, verificationId }),
          });
          if (!verifyRes.ok) throw new Error(`${verifyRes.status}`);
          const verify = (await verifyRes.json()) as { success?: boolean; failureReason?: string | null };
          if (!verify.success) {
            reject(verify.failureReason);
            return;
          }
        }

        // Verified — create / fetch the recipient with this phone. For SMS/voice the
        // verification proof rides along so the server can run its verified-phone reclaim
        // (detach the phone from any other recipient that holds it) instead of dying on the
        // uniqueness index.
        const recipientRes = await fetch(`${API_BASE}/api/recipient`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone,
            phoneVerificationCode: value,
            // WhatsApp carries no verificationId — the recipient service checks the code itself.
            ...(isWhatsApp ? {} : { verificationId }),
            // Names where to celebrate. Absent on the standalone pages, which are not in a conversation
            // yet, so they simply get no card.
            ...(conversationId ? { conversationId } : {}),
          }),
        });
        if (recipientRes.status === 409) {
          setPhoneInUse(true);
          setSubmitting(false);
          return;
        }
        if (isWhatsApp && recipientRes.status === 400) {
          const failure = (await recipientRes.json().catch(() => ({}))) as { failureReason?: string | null };
          reject(failure.failureReason);
          return;
        }
        if (!recipientRes.ok) throw new Error(`recipient ${recipientRes.status}`);
        // The number just landed on the recipient, so every cached copy of it is now wrong. Nothing
        // else refetches it: the shared client does not refetch on focus or mount, and the recipient
        // query stops polling the moment it holds data — so a reader would serve "no phone" for the
        // rest of the session. Both exits below need this, the in-app one because it stays on the page
        // it verified from, and the page one because navigating home is a client push that keeps the
        // cache. What goes stale today is the rail's connect destination; anything else keyed on the
        // number inherits the same fix.
        await queryClient.invalidateQueries({ queryKey: settingsKeys.recipient });
        // Signup. The primary trigger is the fresh-account path (ensureMe → logSignupIfNewAccount);
        // this phone-verification path is the fallback for when the account age is unavailable. Both
        // share one per-account latch, so signup fires at most once. ensureMe also pushes the CUID
        // first (so the event attributes to the person) and swallows its own errors, so a failed/slow
        // /api/me never blocks or drops signup.
        const me = await ensureMe();
        logSignupOnce(me?.userId);
        // The phone is now on the recipient, which is the first moment a referral can be recorded (the
        // service refuses without both an email and a phone). Ahead of the branch below because BOTH
        // exits need it — the in-app one returns, so anything after it would only ever run for a user
        // who came through onboarding. Best-effort by construction: it never throws.
        await claimStoredReferral(me?.userId ?? '');
        if (onVerified) {
          // A caller already inside the app: it is not entering, so it neither waits for the home
          // gate to see the recipient nor resets the conversation pointer — the thread it verified
          // from is the one it goes back to.
          onVerified();
          return;
        }
        // Wait for the just-created recipient to be visible to the home gate before the home
        // navigation, or it races the search index and bounces back to onboarding. Clear any
        // prior active-conversation pointer FIRST (the provider persists across this soft nav)
        // so the client's derived onboarding kickoff opens a clean thread.
        await waitForRecipientVisible(API_BASE);
        setActiveConversationId(null);
        router.push(`${basePath}/`);
      } catch (err) {
        console.warn('[verify-code]', err);
        reject(undefined, true);
      } finally {
        inFlightRef.current = false;
      }
    })();

    function reject(failureReason?: string | null, generic = false) {
      setError({
        message:
          !generic && failureReason === 'BAD_CODE'
            ? t('onboarding.code.error.wrongCode')
            : t('onboarding.code.error.generic'),
        kind: 'code',
      });
      setDigits(Array(CODE_LENGTH).fill(''));
      setSubmitting(false);
      inputsRef.current[0]?.focus();
    }
  }

  useEffect(() => {
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete, value, verificationId, phone, router, method]);

  // Resend confirmation clears after 3s, matching mobile.
  useEffect(() => {
    if (resend !== 'sent') return;
    const timer = setTimeout(() => setResend('idle'), 3000);
    return () => clearTimeout(timer);
  }, [resend]);

  // Autofill (SMS one-time-code / WebOTP) delivers the WHOLE code into the first box,
  // so a multi-character value is spread across the remaining boxes rather than
  // truncated to its first digit.
  function setDigit(i: number, ch: string) {
    const cleaned = ch.replace(/\D/g, '').slice(0, CODE_LENGTH - i);
    setError(null);
    setDigits((prev) => {
      const next = [...prev];
      if (cleaned.length <= 1) {
        next[i] = cleaned;
      } else {
        for (let n = 0; n < cleaned.length; n++) next[i + n] = cleaned[n];
      }
      return next;
    });
    const landed = i + Math.max(cleaned.length, 1);
    if (cleaned) inputsRef.current[Math.min(landed, CODE_LENGTH - 1)]?.focus();
  }

  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    setError(null);
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    inputsRef.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>, i: number) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < CODE_LENGTH - 1) inputsRef.current[i + 1]?.focus();
  }

  // `buttonName` defaults to the single 'resend' control the Orion screen has; the
  // Symphony screen offers SMS and WhatsApp as two separate buttons and names them
  // separately, or the two are indistinguishable in the funnel.
  async function sendCode(nextMethod: CodeMethod, buttonName = 'resend') {
    if (resend === 'sending') return;
    logPageClickBi({ ...PAGE, button_name: buttonName });
    setResend('sending');
    setError(null);
    const isWhatsApp = nextMethod === 'whatsapp';
    const deliveryOption = nextMethod === 'voice' ? 'VOICE_CALL' : isWhatsApp ? 'WHATSAPP' : 'SMS';
    try {
      const { result: data, refused } = await sendVerificationCode(phone, deliveryOption);
      // A refusal is shared-budget exhaustion, not this user resending too often, and it will not clear on
      // another press — so the resend reports it as its own state rather than as a failed send.
      if (refused) {
        logSendCode('refused', deliveryOption);
        setError({ message: t('onboarding.phone.error.sendUnavailable'), kind: 'resend' });
        setResend('idle');
        return;
      }
      // A refused send comes back as HTTP 200, so this has to be asked explicitly.
      if (!data || !wasCodeSent(data, isWhatsApp)) {
        logSendCode('not-sent', deliveryOption);
        setError({
          message: isWhatsApp
            ? t('onboarding.phone.error.whatsappUnavailable')
            : t('onboarding.code.error.resendFailed'),
          kind: 'resend',
        });
        setResend('idle');
        return;
      }
      logSendCode('sent', deliveryOption);
      // Only now adopt the channel: latching it on a failed send left `submit()` posting the
      // digits down the WhatsApp path and discarding a still-valid SMS verificationId.
      if (data.verificationId) setVerificationId(data.verificationId);
      setMethod(nextMethod);
      setResend('sent');
    } catch (err) {
      logSendCode('failed', deliveryOption);
      console.warn('[send-code resend]', err);
      setError({ message: t('onboarding.code.error.resendFailed'), kind: 'resend' });
      setResend('idle');
    }
  }

  return {
    phone,
    method,
    digits,
    setDigit,
    onPaste,
    onKeyDown,
    inputsRef,
    complete,
    submit,
    submitting,
    error: error?.message ?? null,
    errorKind: error?.kind ?? null,
    phoneInUse,
    resend,
    sendCode,
    logPageClickBi: (button_name: string) => logPageClickBi({ ...PAGE, button_name }),
    toPhoneStep: () => (onUseAnotherNumber ? onUseAnotherNumber() : void router.push(`${basePath}/onboarding/phone`)),
  };
}

export type CodeVerification = ReturnType<typeof useCodeVerification>;
